<template>
  <Card>
    <div
      v-if="areEvents"
      :class="$style.activityHeader"
    >
      <div :class="$style.activitySummary">
        <div
          v-if="systemCreate > 0"
          :class="$style.summaryItem"
        >
          <HeadingLarge>
            {{ systemCreate }}
          </HeadingLarge>
          <HeadingMedium
            weight="light"
          >
            Added
          </HeadingMedium>
        </div>
        <div
          v-if="systemDelete > 0"
          :class="$style.summaryItem"
        >
          <HeadingLarge>
            {{ systemDelete }}
          </HeadingLarge>
          <HeadingMedium
            weight="light"
          >
            Deleted
          </HeadingMedium>
        </div>
        <div
          v-if="osMinorUpgrade + osMajorUpgrade > 0"
          :class="$style.summaryItem"
        >
          <HeadingLarge>
            {{ osMinorUpgrade + osMajorUpgrade }}
          </HeadingLarge>
          <HeadingMedium
            weight="light"
          >
            Patched
          </HeadingMedium>
        </div>
        <div
          v-if="osMinorRollback + osMajorRollback > 0"
          :class="$style.summaryItem"
        >
          <HeadingLarge>
            {{ osMinorRollback + osMajorRollback }}
          </HeadingLarge>
          <HeadingMedium
            weight="light"
          >
            Rolled back
          </HeadingMedium>
        </div>
      </div>
    </div>
    <TableWithEndlessScroll
      :class="[$style.systemFleetTable, $style.activityTable, areEvents ? '' : $style.noScroll]"
      :columns="columns"
      :moreToLoad="false"
      :sortBy="sortBy"
      :tableData="tableData"
    >
      <template v-slot:emptyMessage>
        <p>
          No recent activity for this time frame.
        </p>
      </template>
    </TableWithEndlessScroll>
  </Card>
</template>

<script>
import {
  Card, HeadingLarge, HeadingMedium,
} from '@jumpcloud/ui-components';
import { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { eventFields } from 'client/Insights/ActivityLog/constants/EventMetadata';
import { mapActions, mapGetters } from 'vuex';
import { quickPicksOptions } from 'client/Insights/ActivityLog/constants/QuickPicksConsts';
import EventTypes, { eventTypeNameMap } from 'client/Insights/ActivityLog/constants/EventTypes';
import HostnameLink from 'client/components/ItemTable/BodyComponents/HostnameLink';
import TableWithEndlessScroll from 'client/components/Tables/TableWithEndlessScroll';
import moment from 'moment';

const insightsEventModule = StoreModuleMap[ItemTypeConsts.insightsEvent];
const {
  osMinorRollback, osMinorUpgrade, osMajorRollback, osMajorUpgrade, systemCreate, systemDelete,
} = EventTypes;

export default {
  name: 'DevicesActivityTab',

  components: {
    Card, HeadingLarge, HeadingMedium, TableWithEndlessScroll,
  },

  props: {
    daysFilter: {
      type: Number,
      default: 30,
    },
  },

  data: () => ({
    eventsData: [],
    tableData: [],
    columns: [
      { dataField: 'eventType', name: 'Event Type' },
      { dataField: 'timestamp', name: 'Timestamp' },
      {
        bodyComponent: HostnameLink,
        bodyComponentProps: {
          hasTooltip: true,
        },
        name: 'Hostname',
      },
      { dataField: 'details', name: 'Details' },
    ],
    allEventTypes: [
      systemDelete, systemCreate, osMinorUpgrade, osMajorUpgrade, osMinorRollback, osMajorRollback,
    ],
    sortBy: 'timestamp',
    systemCreate: 0,
    systemDelete: 0,
    osMinorUpgrade: 0,
    osMajorUpgrade: 0,
    osMinorRollback: 0,
    osMajorRollback: 0,
  }),

  computed: {
    ...mapGetters(insightsEventModule, ['getEventTypesCount']),

    areEvents() {
      return this.tableData.length > 0;
    },

    dateFilter() {
      let days = 7;
      switch (this.daysFilter) {
        case 1:
          days = quickPicksOptions.twentyFourHours;
          break;
        case 3:
          days = quickPicksOptions.threeDays;
          break;
        case 7:
          days = quickPicksOptions.sevenDays;
          break;
        case 15:
          days = quickPicksOptions.fifteenDays;
          break;
        case 30:
          days = quickPicksOptions.thirtyDays;
          break;
        case 45:
          days = quickPicksOptions.fortyFiveDays;
          break;
        case 60:
          days = quickPicksOptions.sixtyDays;
          break;
        case 90:
          days = quickPicksOptions.ninetyDays;
          break;
        default:
          days = quickPicksOptions.sevenDays;
      }
      return days;
    },
  },

  watch: {
    daysFilter() {
      this.setupEventsData();
    },
  },

  created() {
    this.setupEventsData();
  },

  methods: {
    ...mapActions(insightsEventModule, [
      'clearFilters',
      'fetchEventsCount',
      'fetchEventsData',
      'refreshDates',
      'setFilter',
      'setEventTypeFound',
      'setQuickPickDate',
    ]),

    filterEventsResponse() {
      this.tableData = this.eventsData.map(event => ({
        details: this.getEventDetails(event),
        eventType: eventTypeNameMap[event.event_type],
        timestamp: moment(event.timestamp).utc().fromNow(),
        event,
      }));
    },

    getEventDetails(event) {
      let detailText = '';
      switch (event.event_type) {
        case osMinorRollback:
        case osMajorRollback:
          detailText = `Decrease to ${event.changes[0].to}`;
          break;
        case osMinorUpgrade:
        case osMajorUpgrade:
          detailText = `Upgraded to ${event.changes[0].to}`;
          break;
        case systemCreate:
          if (event.initiated_by.type === 'mdm') {
            detailText = 'Enrolled via MDM';
            break;
          } else {
            detailText = `Added by JumpCloud ${event.initiated_by.type === 'admin' ? 'admin'
              : 'user'} ${event.initiated_by.email}`;
          }
          break;
        case systemDelete:
          detailText = `Deleted by JumpCloud admin ${event.initiated_by.email}`;
          break;
        default:
          break;
      }

      return detailText;
    },

    async setupEventsData() {
      // Reset Event Frequency filters
      this.setEventTypeFound({ filter: '', reset: true });
      // Get event types count
      this.allEventTypes.forEach(async (eventType) => {
        this.setQuickPickDate(this.dateFilter);
        this.clearFilters();
        this.refreshDates();
        this.setFilter({
          field: eventFields.eventType,
          value: eventType,
        });

        const { data } = await this.fetchEventsCount({ limit: 1, eventType });

        if (data.count > 0) {
          this.setEventTypeFound({ filter: eventType });
        }

        switch (eventType) {
          case systemDelete:
            this.systemDelete = data.count;
            break;
          case systemCreate:
            this.systemCreate = data.count;
            break;
          case osMinorRollback:
            this.osMinorRollback = data.count;
            break;
          case osMajorRollback:
            this.osMajorRollback = data.count;
            break;
          case osMinorUpgrade:
            this.osMinorUpgrade = data.count;
            break;
          case osMajorUpgrade:
            this.osMajorUpgrade = data.count;
            break;
          default:
            break;
        }
      });

      // Get events data
      this.clearFilters();
      this.setQuickPickDate(this.dateFilter);
      this.refreshDates();
      this.setFilter({
        field: eventFields.eventType,
        value: this.allEventTypes,
      });

      const events = await this.fetchEventsData({ limit: 50 });

      this.eventsData = events.data || [];
      this.filterEventsResponse();
    },
  },
};

</script>

<style module>
@import './FleetDistributionCommon.css';

.activityHeader {
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  width: 100%;
}

.activityLogLink {
  align-items: center;
  display: flex;
  flex: 2;
  justify-content: flex-end;
}

.activitySummary {
  align-items: baseline;
  display: flex;
  flex: 3;
}

.activityTable {
  width: 100%;
}

.activityTable thead th:last-child,
.activityTable tbody tr td:last-child {
  text-align: left;
}

.noScroll {
  /* !important used to override TableWithEndlessScroll style https://github.com/TheJumpCloud/jumpcloud-admin-portal/blob/86880f137117382a45af974f633bb49c299b5eec/client/components/Tables/TableWithEndlessScroll.vue#L158 */
  overflow: hidden !important;
}

.systemFleetTable tbody tr td:nth-child(3) a,
.systemFleetTable tbody tr td:nth-child(3) a:hover {
  color: var(--jc-color-informational-base);
}

.summaryItem {
  align-items: baseline;
  display: flex;
  gap: var(--jc-spacer-small);
  margin-right: 1.5rem;
}

</style>
